import React, { useState, useEffect } from 'react';
import { usePIM } from '../../context/PIMContext';
import { useToast } from '../../context/ToastContext';
import { X, Search, Link as LinkIcon, FileText, Check } from 'lucide-react';

export const LinkDocumentModal = ({ isOpen, onClose }) => {
    const { toast } = useToast();
    const { fetchSystemDocuments, linkDocumentsToCharge, activeCharge, productData, loadProducts } = usePIM();
    
    const [documents, setDocuments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedDocs, setSelectedDocs] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
            setSelectedDocs([]);
            loadDocuments();
        }
    }, [isOpen]);

    const loadDocuments = async () => {
        setLoading(true);
        const docs = await fetchSystemDocuments();
        setDocuments(docs || []);
        setLoading(false);
    };

    const toggleSelect = (docId) => {
        setSelectedDocs(prev => 
            prev.includes(docId) 
                ? prev.filter(id => id !== docId)
                : [...prev, docId]
        );
    };

    const toggleSelectAll = (filteredIds) => {
        const allSelected = filteredIds.every(id => selectedDocs.includes(id));
        if (allSelected) {
            setSelectedDocs(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            const newSelection = [...new Set([...selectedDocs, ...filteredIds])];
            setSelectedDocs(newSelection);
        }
    };

    const handleLinkSelected = async () => {
        if (!activeCharge || !activeCharge.id) {
            toast.error("No active charge selected. Please save the product/charge first.");
            return;
        }
        if (selectedDocs.length === 0) return;

        const success = await linkDocumentsToCharge(activeCharge.id, selectedDocs);
        if (success) {
            toast.success(`Successfully linked ${selectedDocs.length} document(s)`);
            await loadProducts(true, true, productData.sku);
            onClose();
        } else {
            toast.error("Failed to link documents.");
        }
    };

    if (!isOpen) return null;

    const filteredDocs = documents.filter(doc => 
        (doc.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (doc.original_filename || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredIds = filteredDocs.map(d => d.id);
    const isAllSelected = filteredIds.length > 0 && filteredIds.every(id => selectedDocs.includes(id));
    const isSomeSelected = filteredIds.some(id => selectedDocs.includes(id));

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] backdrop-blur-sm p-4">
            <div className="bg-[#1C1917] border border-stone-800 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[85vh]">
                
                {/* HEADER */}
                <div className="flex justify-between items-center p-4 border-b border-stone-800">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-400">
                            <LinkIcon size={18} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">Link Existing Documents</h2>
                            <p className="text-xs text-stone-400 leading-tight">Select documents from the system to link to the current charge.</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-white transition-colors p-1 hover:bg-stone-800 rounded-md">
                        <X size={20} />
                    </button>
                </div>

                {/* SEARCH BAR */}
                <div className="p-4 border-b border-stone-800 bg-stone-900/50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={16} />
                        <input 
                            type="text" 
                            className="w-full bg-stone-950 border border-stone-800 rounded-lg pl-9 pr-4 py-2.5 text-sm text-stone-200 focus:outline-none focus:border-stone-600 transition-colors placeholder:text-stone-600"
                            placeholder="Search documents by name or filename..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* CONTENT */}
                <div className="p-0 overflow-y-auto flex-1 custom-scrollbar">
                    {loading ? (
                        <div className="p-8 text-center text-stone-500 flex flex-col items-center gap-3">
                            <div className="w-6 h-6 border-2 border-stone-700 border-t-blue-500 rounded-full animate-spin"></div>
                            <span>Loading documents...</span>
                        </div>
                    ) : (
                        <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 bg-stone-900 border-b border-stone-800 z-10">
                                <tr>
                                    <th className="px-4 py-3 w-10">
                                        <div 
                                            className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${
                                                isAllSelected ? 'bg-blue-500 border-blue-500 text-white' : 
                                                isSomeSelected ? 'bg-blue-500/20 border-blue-500 text-blue-400' : 'border-stone-600 hover:border-stone-500'
                                            }`}
                                            onClick={() => toggleSelectAll(filteredIds)}
                                        >
                                            {(isAllSelected || isSomeSelected) && <Check size={12} />}
                                        </div>
                                    </th>
                                    <th className="px-2 py-3 text-xs font-semibold text-stone-500 uppercase">Document Name</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase">File</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Source SKU</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDocs.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-stone-500">No documents found.</td>
                                    </tr>
                                ) : (
                                    filteredDocs.map(doc => {
                                        const isSelected = selectedDocs.includes(doc.id);
                                        return (
                                            <tr 
                                                key={doc.id} 
                                                className={`border-b border-stone-800/50 transition-colors cursor-pointer group ${isSelected ? 'bg-blue-500/5' : 'hover:bg-stone-800/30'}`}
                                                onClick={() => toggleSelect(doc.id)}
                                            >
                                                <td className="px-4 py-3 w-10">
                                                    <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                                        isSelected ? 'bg-blue-500 border-blue-500 text-white' : 'border-stone-600 group-hover:border-stone-400'
                                                    }`}>
                                                        {isSelected && <Check size={12} />}
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3 text-sm text-stone-200 font-medium">
                                                    <div className="flex items-center gap-2">
                                                        <FileText size={14} className={isSelected ? 'text-blue-400' : 'text-stone-500'} />
                                                        {doc.name}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-stone-400 truncate max-w-[200px]" title={doc.original_filename}>
                                                    {doc.original_filename}
                                                </td>
                                                <td className="px-4 py-3 text-xs text-stone-400">
                                                    <div className="flex flex-col">
                                                        <span className="text-stone-300 font-mono">{doc.sku}</span>
                                                        <span className="text-stone-500 truncate max-w-[150px]">{doc.model_name}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* FOOTER */}
                <div className="p-4 border-t border-stone-800 bg-[#1C1917] flex justify-between items-center">
                    <span className="text-sm text-stone-400">
                        {selectedDocs.length} {selectedDocs.length === 1 ? 'document' : 'documents'} selected
                    </span>
                    <div className="flex gap-3">
                        <button 
                            onClick={onClose}
                            className="px-4 py-2 border border-stone-700 bg-stone-800/50 hover:bg-stone-700 text-stone-200 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button 
                            onClick={handleLinkSelected}
                            disabled={selectedDocs.length === 0}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-500/20 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed transition-all"
                        >
                            Add Selected ({selectedDocs.length})
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
